import React, { useMemo } from "react"
import { WingsMajorCard } from "@wingspans-ui"
import { useIntl } from "react-intl"
import { JsonParam, useQueryParam } from "use-query-params"
import { useAllMajors } from "../../data/graphql"
import {
  localizePathwayCheckboxTranslation,
  localizePersonalityCheckboxTranslation,
} from "../algolia/localized-algolia-checkboxes"
import ExploreUnifiedFiltersDropdown from "./ExploreUnifiedFiltersDropdown"
import LocalExplore from "./LocalExplore"

const MAJORS_PER_PAGE = 9

const FILTER_KEYS = {
  personality: "major_personality_filter",
  pathways: "major_pathways_filter",
}
const FILTER_ID_SEPARATOR = "::"
const ENABLE_EXCLUSIVE_OPTION_HIDING = false

const REFINEMENT_FIELDS = [FILTER_KEYS.personality, FILTER_KEYS.pathways]

const majorsSearchKeys = [
  { name: "title ", weight: 0.5 },
  { name: "major_keywords_text", weight: 0.3 },
  { name: "major_job_categories_text", weight: 0.15 },
  { name: "major_pathways_text", weight: 0.05 },
]

const normalizeText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).join(" ")
  }

  if (value && typeof value === "object") {
    return normalizeText(Object.values(value))
  }

  if (value === null || value === undefined) {
    return ""
  }

  return String(value).toLowerCase().trim()
}

const getQueryTokens = (query) => {
  const normalizedQuery = normalizeText(query)

  if (!normalizedQuery) {
    return { normalizedQuery: "", tokens: [] }
  }

  return {
    normalizedQuery,
    tokens: normalizedQuery.split(/\s+/).filter(Boolean),
  }
}

const getMajorTitleQueryPriority = (major, query) => {
  const { normalizedQuery, tokens } = getQueryTokens(query)
  if (!normalizedQuery || !tokens.length) {
    return 0
  }

  const normalizedTitle = normalizeText(major?.title)
  if (!normalizedTitle) {
    return 0
  }
  const isGeneralMajor = normalizedTitle.includes("general")

  if (normalizedTitle === normalizedQuery) {
    return 6
  }

  if (normalizedTitle.startsWith(normalizedQuery)) {
    return isGeneralMajor ? 5 : 4
  }

  const titleParts = normalizedTitle.split(/[^a-z0-9]+/).filter(Boolean)
  if (titleParts.some((part) => part === normalizedQuery)) {
    return 3
  }

  if (titleParts.some((part) => part.startsWith(normalizedQuery))) {
    return 2
  }

  const hasAllTokens = tokens.every((token) => normalizedTitle.includes(token))
  return hasAllTokens ? 1 : 0
}

const toCategoryText = (categories) => {
  if (Array.isArray(categories)) {
    return categories.map((item) => item?.title || item?.value || item).filter(Boolean).join(" ")
  }

  if (categories && typeof categories === "object") {
    return categories.title || categories.value || ""
  }

  return categories || ""
}

const normalizeRefinementList = (refinementList) => {
  const normalized = {
    [FILTER_KEYS.personality]: [],
    [FILTER_KEYS.pathways]: [],
  }

  if (!refinementList || typeof refinementList !== "object") {
    return normalized
  }

  Object.keys(normalized).forEach((key) => {
    const values = refinementList[key]
    if (Array.isArray(values)) {
      normalized[key] = values
      return
    }

    normalized[key] = typeof values === "string" && values ? [values] : []
  })

  return normalized
}

const getLocalizedLabel = (value, translationMap, locale) => {
  return translationMap?.[value]?.[locale] || value
}

const toArray = (value) => {
  if (!value) {
    return []
  }

  return Array.isArray(value) ? value.filter(Boolean) : [value]
}

const matchesSelection = (value, selectedValues) => {
  if (!selectedValues.length) {
    return true
  }

  return toArray(value).some((item) => selectedValues.includes(item))
}

const majorMatchesRefinements = (major, refinementList) => {
  return matchesSelection(major[FILTER_KEYS.personality], refinementList[FILTER_KEYS.personality])
    && matchesSelection(major[FILTER_KEYS.pathways], refinementList[FILTER_KEYS.pathways])
}

const getFilterCounts = (items, field, translationMap, locale) => {
  const counts = new Map()

  items.forEach((item) => {
    toArray(item[field]).forEach((value) => {
      counts.set(value, (counts.get(value) || 0) + 1)
    })
  })

  return Array.from(counts.entries())
    .map(([value, count]) => ({
      value,
      label: getLocalizedLabel(value, translationMap, locale),
      count,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

const ensureSelectedOptionsVisible = (options, selectedValues, translationMap, locale) => {
  const optionMap = new Map(options.map((option) => [option.value, option]))

  selectedValues.forEach((value) => {
    if (!optionMap.has(value)) {
      optionMap.set(value, {
        value,
        label: getLocalizedLabel(value, translationMap, locale),
        count: 0,
      })
    }
  })

  return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label))
}

const mapMajorToCard = (major) => {
  const categoryText = major.major_job_categories_text || toCategoryText(major.major_job_categories)

  return (
    <WingsMajorCard
      objectID={major.id}
      img={major.major_banner_image}
      title={major.title}
      slug={major.slug}
      category={categoryText}
      contentType={major.type}
      bachelor={major.major_bachelor}
      associate={major.major_associate}
      graduate={major.major_graduate}
      master={major.major_master}
      doctorate={major.major_doctorate}
    />
  )
}

const hasDegreeOption = (major) => {
  return Boolean(
    major.major_bachelor
      || major.major_associate
      || major.major_graduate
      || major.major_master
      || major.major_doctorate
  )
}

const getMajorRecencyDate = (major) => {
  return major?.modified_at
    || major?.published_at
}

const MajorsExplore = ({ classContext = "majors", filterList }) => {
  const { locale, formatMessage } = useIntl()
  const [queryFilters, setQueryFilters] = useQueryParam("queryFilters", JsonParam)
  const majors = useAllMajors(locale)

  const majorsWithSearchText = majors
    .filter(hasDegreeOption)
    .map((major) => ({
      ...major,
      major_keywords_text: Array.isArray(major.major_keywords)
        ? major.major_keywords.filter(Boolean).join(" ")
        : (major.major_keywords || ""),
      major_job_categories_text: toCategoryText(major.major_job_categories),
      major_pathways_text: Array.isArray(major.major_pathways_filter)
        ? major.major_pathways_filter.join(" ")
        : (major.major_pathways_filter || ""),
    }))

  const selectedRefinements = useMemo(() => {
    return normalizeRefinementList(queryFilters?.refinementList)
  }, [queryFilters])

  const getMajorsMatchingOtherRefinements = (excludedField) => {
    const refinementListWithoutField = REFINEMENT_FIELDS.reduce((acc, field) => {
      acc[field] = field === excludedField ? [] : (selectedRefinements[field] || [])
      return acc
    }, {})

    return majorsWithSearchText.filter((major) => majorMatchesRefinements(major, refinementListWithoutField))
  }

  const personalityOptions = useMemo(() => {
    const matchingMajors = ENABLE_EXCLUSIVE_OPTION_HIDING
      ? getMajorsMatchingOtherRefinements(FILTER_KEYS.personality)
      : majorsWithSearchText
    const options = getFilterCounts(matchingMajors, FILTER_KEYS.personality, localizePersonalityCheckboxTranslation, locale)

    return ensureSelectedOptionsVisible(
      options,
      selectedRefinements[FILTER_KEYS.personality],
      localizePersonalityCheckboxTranslation,
      locale
    )
  }, [majorsWithSearchText, selectedRefinements, locale])

  const pathwaysOptions = useMemo(() => {
    const matchingMajors = ENABLE_EXCLUSIVE_OPTION_HIDING
      ? getMajorsMatchingOtherRefinements(FILTER_KEYS.pathways)
      : majorsWithSearchText
    const options = getFilterCounts(matchingMajors, FILTER_KEYS.pathways, localizePathwayCheckboxTranslation, locale)

    return ensureSelectedOptionsVisible(
      options,
      selectedRefinements[FILTER_KEYS.pathways],
      localizePathwayCheckboxTranslation,
      locale
    )
  }, [majorsWithSearchText, selectedRefinements, locale])

  const filteredMajors = useMemo(() => {
    return majorsWithSearchText.filter((major) => majorMatchesRefinements(major, selectedRefinements))
  }, [majorsWithSearchText, selectedRefinements])

  const toggleRefinement = (filterKey, value, isChecked) => {
    const currentRefinements = normalizeRefinementList(queryFilters?.refinementList)
    const currentValues = currentRefinements[filterKey] || []
    const nextValues = isChecked
      ? [...new Set([...currentValues, value])]
      : currentValues.filter((item) => item !== value)

    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: {
        ...currentRefinements,
        [filterKey]: nextValues,
      },
    })
  }

  const clearRefinements = () => {
    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: {},
    })
  }

  const selectedFilterOptionIds = useMemo(() => {
    return Object.entries(selectedRefinements).flatMap(([filterKey, values]) => {
      return (values || []).map((value) => `${filterKey}${FILTER_ID_SEPARATOR}${value}`)
    })
  }, [selectedRefinements])

  const filterSections = useMemo(() => {
    return [
      {
        id: FILTER_KEYS.personality,
        title: formatMessage({ id: "majors-explore-personality", defaultMessage: "Personality" }),
        columns: 3,
        options: personalityOptions.map((option) => ({
          id: `${FILTER_KEYS.personality}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: FILTER_KEYS.pathways,
        title: formatMessage({ id: "majors-explore-career-pathway", defaultMessage: "Career Pathway" }),
        columns: 3,
        options: pathwaysOptions.map((option) => ({
          id: `${FILTER_KEYS.pathways}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
    ]
  }, [formatMessage, personalityOptions, pathwaysOptions])

  const handleUnifiedFilterToggle = (optionId) => {
    const [filterKey, value] = optionId.split(FILTER_ID_SEPARATOR)

    if (!filterKey || !value) {
      return
    }

    const isChecked = selectedRefinements[filterKey]?.includes(value)
    toggleRefinement(filterKey, value, !isChecked)
  }

  return (
    <LocalExplore
      classContext={classContext}
      filterList={filterList}
      items={filteredMajors}
      searchKeys={majorsSearchKeys}
      hitsPerPage={MAJORS_PER_PAGE}
      preserveSearchRelevanceOrder={true}
      getQueryPriority={getMajorTitleQueryPriority}
      getRecencyDate={getMajorRecencyDate}
      searchPlaceholder={formatMessage({
        id: "algolia-majors-explore-search-placeholder",
        defaultMessage: "Search by keyword, major title, category, pathway",
      })}
      renderCard={mapMajorToCard}
      renderSortExtras={() => {
        return (
          <ExploreUnifiedFiltersDropdown
            heading={formatMessage({ id: "majors-explore-filter-majors-by", defaultMessage: "Filter Majors by" })}
            viewLabel={formatMessage({ id: "algolia-toggle-dropdown-majors", defaultMessage: "Majors" })}
            sections={filterSections}
            selectedOptionIds={selectedFilterOptionIds}
            onToggleOption={handleUnifiedFilterToggle}
            onClearAll={clearRefinements}
          />
        )
      }}
    />
  )
}

export default MajorsExplore
